package com.example.mystybag.appCore.helpers

import com.example.mystybag.communication.Communication
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.ext.CommandResult
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_CODE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_DELETE_ACCOUNT_REQUEST_PENDING
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_NOTIFICATIONS_DISCOUNTS_AND_NEWS_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_NOTIFICATIONS_DISCOUNTS_AND_NEWS_PUSH
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_NOTIFICATIONS_RESERVATION_COMPLETED_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_NOTIFICATIONS_RESERVATION_COMPLETED_PUSH
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_PASSWORD
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_CREATE_BAGS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_VIEW_REVENUE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_PASSWORD
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.helpers.GlobalCommonData
import com.example.mystybag.core.security.Security
import com.example.mystybag.sharedmodels.defines.MerchantStatusDefines.Companion.MERCHANT_STATUS_UNDER_REVIEW
import com.example.mystybag.sharedmodels.project.requestModels.LoginUserRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.LoginMerchantRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.LoginStaffRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.LoginUserResponseModel

/**
 * При 401:
 * - Търговец/служител: отваря се екран за логин (без автоматичен re-login).
 * - Потребител (client): опит за автоматичен re-login със запазени credentials и повтор на заявката.
 */
object ReLoginHelper {

    private fun applyClientLoginSuccess(response: LoginUserResponseModel, fallbackEmail: String) {
        Communication.token = response.token
        Communication.isLoginSuccess = true
        Preferences.setString(PREFERENCES_JWT, response.token)
        Preferences.setString(PREFERENCES_MERCHANT_JWT, "")
        val emailToStore = if (response.email.isNotEmpty()) response.email else fallbackEmail
        Preferences.setString(PREFERENCES_EMAIL, emailToStore)
        GlobalCommonData.setEmailValue(emailToStore)
        GlobalCommonData.setUserIdValue(response.userId)
        GlobalCommonData.setFirstNameValue(response.firstName)
        GlobalCommonData.setLastNameValue(response.lastName)
        GlobalCommonData.setUserImgUrlValue(response.userImgUrl)
        GlobalCommonData.setDialCodeValue(response.dialCode)
        GlobalCommonData.setPhoneNumberValue(response.phoneNumber)
        GlobalCommonData.setNumberOfReservationsValue(response.numberOfReservations)
        GlobalCommonData.setSavedAmountValue(response.savedAmount)
        Preferences.setBoolean(PREFERENCES_NOTIFICATIONS_RESERVATION_COMPLETED_PUSH, response.reservationCompletedPushEnabled)
        Preferences.setBoolean(PREFERENCES_NOTIFICATIONS_RESERVATION_COMPLETED_EMAIL, response.reservationCompletedEmailEnabled)
        Preferences.setBoolean(PREFERENCES_NOTIFICATIONS_DISCOUNTS_AND_NEWS_PUSH, response.discountsAndNewsPushEnabled)
        Preferences.setBoolean(PREFERENCES_NOTIFICATIONS_DISCOUNTS_AND_NEWS_EMAIL, response.discountsAndNewsEmailEnabled)
        Preferences.setBoolean(PREFERENCES_DELETE_ACCOUNT_REQUEST_PENDING, response.hasPendingDeleteAccountRequest)
    }

    /**
     * След успешно платена резервация – синхронизира от сървъра броя резервации и спестената сума.
     * Само за клиентска сесия (няма активен merchant JWT).
     */
    suspend fun refreshClientProfileAfterPaidReservation(): Boolean {
        val merchantJwt = Preferences.getString(key = PREFERENCES_MERCHANT_JWT, "")
        if (merchantJwt.isNotEmpty()) return false
        val email = Preferences.getString(key = PREFERENCES_EMAIL, "")
        val code = Preferences.getString(key = PREFERENCES_CODE, "")
        if (email.isEmpty() || code.isEmpty()) return false
        val result = CommonCommands.sendUserLogin(
            LoginUserRequestModel(mail = email, code = code)
        )
        return when (result) {
            is CommandResult.Success -> {
                applyClientLoginSuccess(result.successModel, email)
                true
            }
            else -> false
        }
    }

    /** Извиква се при 401 в сесия като търговец/служител – отваря екрана за логин. Параметърът е true за staff, false за merchant. */
    var onNavigateToMerchantOrStaffLogin: ((isStaff: Boolean) -> Unit)? = null

    /**
     * Ако 401 пристигне преди да е зададен callback-ът (напр. преди ViewModel init), запазваме да изкараме към логин веднага щом callback се зададе.
     */
    var pendingNavigateToMerchantOrStaffLogin: Boolean? = null
        private set

    /**
     * Обработка на 401: при търговец/служител – изчистване на сесията и навигация към логин; при потребител – опит за tryReLogin и повтор на заявката.
     * Търговец/служител се счита и когато JWT е празен, но има запазен merchant или staff email (напр. след рестарт или изтичане на токена).
     */
    suspend fun handleUnauthorized(): Boolean {
        val merchantJwt = Preferences.getString(key = PREFERENCES_MERCHANT_JWT, "")
        val merchantEmail = Preferences.getString(key = PREFERENCES_MERCHANT_EMAIL, "")
        val isStaffPref = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
        val staffEmail = Preferences.getString(key = PREFERENCES_STAFF_EMAIL, "")

        val isMerchantOrStaffSession = merchantJwt.isNotEmpty() ||
            merchantEmail.isNotEmpty() ||
            (isStaffPref && staffEmail.isNotEmpty())

        if (isMerchantOrStaffSession) {
            // Сесия като търговец или служител – изчистваме сесията и отваряме екрана за логин (Staff ако има запазен staff email, иначе Merchant)
            val isStaff = isStaffPref && staffEmail.isNotEmpty()
            Communication.token = ""
            Preferences.setString(PREFERENCES_MERCHANT_JWT, "")
            Preferences.setLong(PREFERENCES_MERCHANT_ID, 0L)
            Preferences.setBoolean(PREFERENCES_IS_STAFF, false)
            Preferences.setString(PREFERENCES_STAFF_LOCATION_IDS, "")
            Preferences.setBoolean(PREFERENCES_STAFF_CAN_CREATE_BAGS, false)
            Preferences.setBoolean(PREFERENCES_STAFF_CAN_VIEW_REVENUE, false)
            val callback = onNavigateToMerchantOrStaffLogin
            if (callback != null) {
                callback.invoke(isStaff)
            } else {
                pendingNavigateToMerchantOrStaffLogin = isStaff
            }
            return false
        }
        return tryReLogin()
    }

    /** Извиква се от ViewModel при задаване на callback – ако има pending 401 за търговец/служител, веднага навигира към логин. */
    fun applyPendingMerchantOrStaffNavigationIfNeeded(callback: (isStaff: Boolean) -> Unit) {
        val pending = pendingNavigateToMerchantOrStaffLogin ?: return
        pendingNavigateToMerchantOrStaffLogin = null
        callback(pending)
    }

    suspend fun tryReLogin(): Boolean {
        // 1) Опит за staff re-login (email + запазена парола), ако потребителят е бил влезъл като staff
        val isStaff = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
        val staffEmail = Preferences.getString(key = PREFERENCES_STAFF_EMAIL, "")
        val staffPassword = Preferences.getSecureString(key = PREFERENCES_STAFF_PASSWORD, defaultValue = "")
        if (isStaff && staffEmail.isNotEmpty() && staffPassword.isNotEmpty()) {
            val result = CommonCommands.loginStaff(
                LoginStaffRequestModel(
                    email = staffEmail,
                    password = Security.hashPasswordForRequest(staffPassword)
                )
            )
            when (result) {
                is CommandResult.Success -> {
                    val loginResponse = result.successModel
                    Communication.token = loginResponse.jwt
                    Communication.isLoginSuccess = true
                    Preferences.setString(PREFERENCES_MERCHANT_JWT, loginResponse.jwt)
                    Preferences.setLong(PREFERENCES_MERCHANT_ID, loginResponse.merchantId)
                    Preferences.setBoolean(PREFERENCES_IS_STAFF, true)
                    Preferences.setString(
                        PREFERENCES_STAFF_LOCATION_IDS,
                        loginResponse.merchantLocationIds.joinToString(",")
                    )
                    Preferences.setBoolean(
                        PREFERENCES_STAFF_CAN_CREATE_BAGS,
                        loginResponse.canCreateBags
                    )
                    Preferences.setBoolean(
                        PREFERENCES_STAFF_CAN_VIEW_REVENUE,
                        loginResponse.canViewRevenue
                    )
                    return true
                }
                else -> { /* fall through to merchant or user login */ }
            }
        }

        // 2) Опит за merchant re-login (email + запазена парола)
        val merchantEmail = Preferences.getString(key = PREFERENCES_MERCHANT_EMAIL, "")
        val merchantPassword = Preferences.getSecureString(key = PREFERENCES_MERCHANT_PASSWORD, defaultValue = "")
        if (merchantEmail.isNotEmpty() && merchantPassword.isNotEmpty()) {
            val result = CommonCommands.loginMerchant(
                LoginMerchantRequestModel(
                    email = merchantEmail,
                    password = Security.hashPasswordForRequest(merchantPassword)
                )
            )
            when (result) {
                is CommandResult.Success -> {
                    if (result.successModel.merchantStatus == MERCHANT_STATUS_UNDER_REVIEW) {
                        return false
                    }
                    Communication.token = result.successModel.jwt
                    Communication.isLoginSuccess = true
                    Preferences.setString(PREFERENCES_MERCHANT_JWT, result.successModel.jwt)
                    Preferences.setLong(PREFERENCES_MERCHANT_ID, result.successModel.merchantId)
                    Preferences.setBoolean(PREFERENCES_IS_STAFF, false)
                    Preferences.setString(PREFERENCES_STAFF_LOCATION_IDS, "")
                    Preferences.setBoolean(PREFERENCES_STAFF_CAN_CREATE_BAGS, false)
                    Preferences.setBoolean(PREFERENCES_STAFF_CAN_VIEW_REVENUE, false)
                    return true
                }
                else -> { /* fall through to user login or return false */ }
            }
        }

        // 3) Опит за user (client) re-login (email + код)
        val email = Preferences.getString(key = PREFERENCES_EMAIL, "")
        val code = Preferences.getString(key = PREFERENCES_CODE, "")
        if (email.isNotEmpty() && code.isNotEmpty()) {
            val result = CommonCommands.sendUserLogin(
                LoginUserRequestModel(mail = email, code = code)
            )
            when (result) {
                is CommandResult.Success -> {
                    applyClientLoginSuccess(result.successModel, email)
                    return true
                }
                else -> { /* fall through */ }
            }
        }

        return false
    }
}
